import type { Product } from "@/contexts/product-context"
import { getLoginSession } from "@/lib/login-session"
import {
  productHomeSlug,
  productLabel,
  visibleWhatsNew,
  type ProductHomeCard,
} from "@/lib/product-home"
import { getProductBrand } from "@/lib/product-brand"
import { findCatalogEntry, isProductEntitled } from "@/lib/mock/product-catalog"
import { productGlyph } from "@/lib/product-glyph"

/** Seeded sign-in flow for site-side Exxat One + Surveys demo. */
export const ONE_SITE_DEMO_FLOW_ID = "role-one-site"

export const ONE_SITE_QA_LAUNCH_URL = "https://qaone.exxat.com/site/launch"

/** Hosted Surveys app (Pages deploy), without query params. */
export const ONE_SITE_SURVEYS_EMBED_URL =
  "https://exxat-surveys-24f.pages.dev/surveys/dashboard"

export const ONE_SITE_LAUNCH_PATH = "/one-sites/launch"
/** Sidebar nav uses `/{slug}/dashboard`; iframe also mounts on surveys-dashboard. */
export const ONE_SITE_SURVEYS_PATH = "/surveys/dashboard"
export const ONE_SITE_SURVEYS_ALT_PATH = "/surveys/surveys-dashboard"

const ONE_SITE_PRODUCTS: readonly Product[] = ["exxat-one-sites", "exxat-surveys"]

function hasOneSiteProductGrant(session: ReturnType<typeof getLoginSession>): boolean {
  const products = session.products
  if (!products || products.length !== 2) return false
  return (
    products.includes("exxat-one-sites") &&
    products.includes("exxat-surveys") &&
    session.showMoreFromExxat === false
  )
}

export function isOneSiteDemoFlow(): boolean {
  const session = getLoginSession()
  if (session.demoFlowId === ONE_SITE_DEMO_FLOW_ID) return true
  return hasOneSiteProductGrant(session)
}

export function isSurveysHostedDashboardPath(pathname: string): boolean {
  return pathname === ONE_SITE_SURVEYS_PATH || pathname === ONE_SITE_SURVEYS_ALT_PATH
}

/** Iframe src for the in-shell Surveys dashboard route (One Site role). */
export function surveysHostedEmbedSrc(): string {
  return ONE_SITE_SURVEYS_EMBED_URL
}

export function isOneSiteDemoEmbedPath(pathname: string): boolean {
  if (!isOneSiteDemoFlow()) return false
  return pathname === ONE_SITE_LAUNCH_PATH || pathname === "/one-sites/dashboard"
}

/** Product switch and dashboard entry for the Exxat One Site demo role. */
export function oneSiteDemoLandingPath(product: Product): string | null {
  if (!isOneSiteDemoFlow()) return null
  if (product === "exxat-one-sites") return ONE_SITE_LAUNCH_PATH
  return null
}

export function oneSiteDemoHostedFrame(
  product: Product,
): { src: string; title: string } | null {
  if (!isOneSiteDemoFlow()) return null
  if (product === "exxat-one-sites") {
    return { src: ONE_SITE_QA_LAUNCH_URL, title: "Exxat One site launch" }
  }
  return null
}

function catalogCard(product: Product, href: string): ProductHomeCard | null {
  const entry = findCatalogEntry(product)
  if (!entry || !isProductEntitled(product)) return null
  return {
    product: entry.product,
    label: productLabel(entry.product),
    brandColor: getProductBrand(entry.product)?.brandColor ?? "var(--brand-color)",
    tagline: entry.tagline,
    scopeLabel: entry.scopeLabel,
    icon: productGlyph(entry.product),
    scoped: entry.scoped,
    entitled: true,
    stage: entry.stage,
    whatsNew: visibleWhatsNew(entry.whatsNew),
    entry,
    slug: productHomeSlug(entry.product),
    href,
  }
}

/** Owned tiles for the One Site demo (Exxat One Sites is normally off the home grid). */
export function oneSiteDemoOwnedCards(): ProductHomeCard[] {
  if (!isOneSiteDemoFlow()) return []
  const card = catalogCard("exxat-one-sites", ONE_SITE_LAUNCH_PATH)
  return card ? [card] : []
}

/** Apply demo launch hrefs when opening from home or switcher inventory. */
export function applyOneSiteDemoCardHrefs<T extends ProductHomeCard>(cards: T[]): T[] {
  if (!isOneSiteDemoFlow()) return cards
  return cards.map(card => {
    if (card.product === "exxat-one-sites") {
      return { ...card, href: ONE_SITE_LAUNCH_PATH }
    }
    if (card.product === "exxat-surveys") {
      return { ...card, href: ONE_SITE_SURVEYS_EMBED_URL }
    }
    return card
  })
}
